import React, { useState, useEffect, useCallback } from 'react';
import {
  taskAPI,
  userAPI,
  TaskSummary,
  TaskCategory,
  TaskDashboardSummary,
  TaskFilters,
  TaskSeverity,
  TaskStatus,
} from '../services/api';
import TaskDetailModal from '../components/TaskDetailModal';
import CreateTaskModal from '../components/CreateTaskModal';
import AlertDetailModal from '../components/AlertDetailModal';
import { usePermissions } from '../hooks/usePermissions';

const SEVERITY_COLORS: Record<TaskSeverity, { bg: string; text: string; label: string }> = {
  high: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-300', label: 'High' },
  medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-700 dark:text-yellow-300', label: 'Medium' },
  low: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-300', label: 'Low' },
};

const STATUS_COLORS: Record<TaskStatus, { bg: string; text: string; label: string }> = {
  not_started: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: 'Not Started' },
  in_progress: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-300', label: 'In Progress' },
  completed: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-300', label: 'Completed' },
  reassigned: { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-700 dark:text-purple-300', label: 'Reassigned' },
};

const CATEGORY_HEADER_COLORS: Record<string, string> = {
  Quality: 'bg-indigo-600',
  Maintenance: 'bg-amber-600',
  Production: 'bg-emerald-600',
  Safety: 'bg-rose-600',
  Warehouse: 'bg-cyan-600',
};

interface TasksByCategory {
  category: TaskCategory;
  tasks: TaskSummary[];
}

interface User {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
}

const TaskBoard: React.FC = () => {
  // Permissions
  const { user, isViewer, canCreateTask } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [dashboardSummary, setDashboardSummary] = useState<TaskDashboardSummary | null>(null);
  const [tasksByCategory, setTasksByCategory] = useState<TasksByCategory[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Filters
  const [filterSeverity, setFilterSeverity] = useState<TaskSeverity | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<TaskStatus | undefined>(undefined);
  const [filterCategoryId, setFilterCategoryId] = useState<number | undefined>(undefined);
  const [filterAssigneeId, setFilterAssigneeId] = useState<number | undefined>(undefined);
  const [filterDueDateFrom, setFilterDueDateFrom] = useState<string>('');
  const [filterDueDateTo, setFilterDueDateTo] = useState<string>('');
  const [includeCompleted, setIncludeCompleted] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const pageSize = 50;

  // Modal state
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState<number | null>(null);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

  const fetchCategories = async () => {
    try {
      const cats = await taskAPI.getCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const userList = await userAPI.list();
      setUsers(userList.filter(u => u.is_active));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      const summary = await taskAPI.getDashboardSummary();
      setDashboardSummary(summary);
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
    }
  };

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);

      const filters: TaskFilters = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        include_completed: includeCompleted,
      };

      if (filterSeverity) filters.severity = filterSeverity;
      if (filterStatus) filters.status = filterStatus;
      if (filterCategoryId) filters.category_id = filterCategoryId;
      if (filterAssigneeId) {
        filters.assigned_to_user_id = filterAssigneeId;
      }
      if (filterDueDateFrom) filters.due_date_from = filterDueDateFrom;
      if (filterDueDateTo) filters.due_date_to = filterDueDateTo;

      const response = await taskAPI.list(filters);
      setTotalTasks(response.total);

      // Group tasks by category
      const grouped: Record<number, TaskSummary[]> = {};
      categories.forEach(cat => {
        grouped[cat.id] = [];
      });

      response.items.forEach(task => {
        if (grouped[task.category_id]) {
          grouped[task.category_id].push(task);
        }
      });

      const result: TasksByCategory[] = categories.map(cat => ({
        category: cat,
        tasks: grouped[cat.id] || [],
      }));

      setTasksByCategory(result);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [categories, currentPage, filterSeverity, filterStatus, filterCategoryId, filterAssigneeId, filterDueDateFrom, filterDueDateTo, includeCompleted]);

  useEffect(() => {
    fetchCategories();
    fetchUsers();
    fetchDashboardSummary();
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      fetchTasks();
    }
  }, [categories, fetchTasks]);

  const handleFilter = () => {
    setCurrentPage(1);
    fetchTasks();
  };

  const handleResetFilters = () => {
    setFilterSeverity(undefined);
    setFilterStatus(undefined);
    setFilterCategoryId(undefined);
    setFilterAssigneeId(undefined);
    setFilterDueDateFrom('');
    setFilterDueDateTo('');
    setIncludeCompleted(false);
    setCurrentPage(1);
  };

  const handleMyTasks = () => {
    if (user) {
      // Toggle: if already filtering by current user, clear it
      if (filterAssigneeId === user.id) {
        setFilterAssigneeId(undefined);
      } else {
        setFilterAssigneeId(user.id);
      }
      setCurrentPage(1);
    }
  };

  const handleAlertClick = (alertId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click from opening task modal
    setSelectedAlertId(alertId);
    setIsAlertModalOpen(true);
  };

  const handleCloseAlertModal = () => {
    setIsAlertModalOpen(false);
    setSelectedAlertId(null);
  };

  const hasActiveFilters = filterSeverity || filterStatus ||
    filterCategoryId || filterAssigneeId || filterDueDateFrom || filterDueDateTo || includeCompleted;

  const handleTaskClick = (taskId: number) => {
    setSelectedTaskId(taskId);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTaskId(null);
  };

  const handleTaskUpdated = () => {
    fetchTasks();
    fetchDashboardSummary();
  };

  const totalPages = Math.ceil(totalTasks / pageSize);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  };

  // Render category section with tasks
  const renderCategorySection = (categoryData: TasksByCategory) => {
    const { category, tasks } = categoryData;
    const headerColor = CATEGORY_HEADER_COLORS[category.name] || 'bg-gray-600';

    return (
      <div key={category.id} className="mb-4">
        <div className={`${headerColor} text-white px-4 py-2 rounded-t-lg font-bold uppercase tracking-wide`}>
          {category.name}
        </div>
        <div className="border-2 border-t-0 rounded-b-lg overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
          {tasks.length === 0 ? (
            <div className="p-4 text-center" style={{ color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}>
              No tasks in this category
            </div>
          ) : (
            <table className="w-full text-sm" style={{ background: 'var(--bg-secondary)' }}>
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <th className="p-3 text-left font-semibold" style={{ color: 'var(--text-primary)', width: '80px' }}>Alert ID</th>
                  <th className="p-3 text-left font-semibold" style={{ color: 'var(--text-primary)' }}>Task Description</th>
                  <th className="p-3 text-left font-semibold" style={{ color: 'var(--text-primary)', width: '140px' }}>Assigned To</th>
                  <th className="p-3 text-left font-semibold" style={{ color: 'var(--text-primary)', width: '100px' }}>Due Date</th>
                  <th className="p-3 text-left font-semibold" style={{ color: 'var(--text-primary)', width: '100px' }}>Severity</th>
                  <th className="p-3 text-left font-semibold" style={{ color: 'var(--text-primary)', width: '120px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => {
                  const severityStyle = SEVERITY_COLORS[task.severity];
                  const statusStyle = STATUS_COLORS[task.status];

                  return (
                    <tr
                      key={task.id}
                      onClick={() => handleTaskClick(task.id)}
                      className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      <td className="p-3 font-medium">
                        {task.alert_id ? (
                          <button
                            onClick={(e) => handleAlertClick(task.alert_id!, e)}
                            className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                          >
                            #{task.alert_id}
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>-</span>
                        )}
                      </td>
                      <td className="p-3" style={{ color: 'var(--text-primary)' }}>
                        {task.title}
                      </td>
                      <td className="p-3" style={{ color: 'var(--text-primary)' }}>
                        {task.assigned_to_name || '-'}
                      </td>
                      <td className="p-3" style={{ color: 'var(--text-primary)' }}>
                        {formatDate(task.due_date)}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${severityStyle.bg} ${severityStyle.text}`}>
                          {severityStyle.label}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusStyle.bg} ${statusStyle.text}`}>
                          {statusStyle.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>
            Task Board
          </h1>
          {canCreateTask && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 rounded-lg font-semibold bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
            >
              + Create Task
            </button>
          )}
          {isViewer && (
            <span className="text-sm px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800" style={{ color: 'var(--text-secondary)' }}>
              Read-only
            </span>
          )}
        </div>
        {dashboardSummary && (
          <div className="flex gap-4 text-sm">
            <span style={{ color: 'var(--text-secondary)' }}>
              Total: <strong style={{ color: 'var(--text-primary)' }}>{dashboardSummary.total_tasks}</strong>
            </span>
            <span className="text-red-600">
              Overdue: <strong>{dashboardSummary.overdue_tasks}</strong>
            </span>
            <span className="text-yellow-600">
              Due Today: <strong>{dashboardSummary.due_today}</strong>
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-xl shadow-lg p-4 border-2" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
        {/* Basic Filters Row */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Severity:</label>
            <select
              value={filterSeverity || ''}
              onChange={(e) => setFilterSeverity(e.target.value as TaskSeverity || undefined)}
              className="border-2 rounded-lg px-3 py-2 text-sm min-w-[120px]"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
              }}
            >
              <option value="">All</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Status:</label>
            <select
              value={filterStatus || ''}
              onChange={(e) => setFilterStatus(e.target.value as TaskStatus || undefined)}
              className="border-2 rounded-lg px-3 py-2 text-sm min-w-[130px]"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
              }}
            >
              <option value="">All</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="reassigned">Reassigned</option>
            </select>
          </div>

          <button
            onClick={handleFilter}
            className="px-4 py-2 rounded-lg font-semibold bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
          >
            Apply
          </button>

          {/* Quick "My Tasks" filter */}
          {user && (
            <button
              onClick={handleMyTasks}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                filterAssigneeId === user.id
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50'
              }`}
            >
              {filterAssigneeId === user.id && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              My Tasks ({user.full_name?.split(' ')[0] || user.email})
            </button>
          )}

          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="px-4 py-2 rounded-lg font-medium border-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all flex items-center gap-2"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          >
            {showAdvancedFilters ? 'Less Filters' : 'More Filters'}
            <span className={`transform transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`}>&#9662;</span>
          </button>

          {hasActiveFilters && (
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 rounded-lg font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Advanced Filters Row */}
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 items-center" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Category:</label>
              <select
                value={filterCategoryId || ''}
                onChange={(e) => setFilterCategoryId(e.target.value ? Number(e.target.value) : undefined)}
                className="border-2 rounded-lg px-3 py-2 text-sm min-w-[140px]"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                }}
              >
                <option value="">All</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Assigned To:</label>
              <select
                value={filterAssigneeId || ''}
                onChange={(e) => setFilterAssigneeId(e.target.value ? Number(e.target.value) : undefined)}
                className="border-2 rounded-lg px-3 py-2 text-sm min-w-[180px]"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                }}
              >
                <option value="">All Users</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Due From:</label>
              <input
                type="date"
                value={filterDueDateFrom}
                onChange={(e) => setFilterDueDateFrom(e.target.value)}
                className="border-2 rounded-lg px-3 py-2 text-sm"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                }}
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Due To:</label>
              <input
                type="date"
                value={filterDueDateTo}
                onChange={(e) => setFilterDueDateTo(e.target.value)}
                className="border-2 rounded-lg px-3 py-2 text-sm"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                }}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeCompleted}
                onChange={(e) => setIncludeCompleted(e.target.checked)}
                className="w-4 h-4 rounded border-2"
              />
              <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Include Completed</span>
            </label>
          </div>
        )}
      </div>

      {/* Task Categories */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {tasksByCategory.map(categoryData => renderCategorySection(categoryData))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center pt-4">
          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalTasks)} of {totalTasks} entries
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg font-medium border-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              Previous
            </button>
            <span className="px-4 py-2 font-medium" style={{ color: 'var(--text-primary)' }}>
              {currentPage}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg font-medium border-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseModal}
        taskId={selectedTaskId}
        categories={categories}
        onTaskUpdated={handleTaskUpdated}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        categories={categories}
        onTaskCreated={handleTaskUpdated}
      />

      {/* Alert Detail Modal */}
      <AlertDetailModal
        isOpen={isAlertModalOpen}
        onClose={handleCloseAlertModal}
        alertId={selectedAlertId}
      />
    </section>
  );
};

export default TaskBoard;
